import { create } from 'zustand';
import { namedRegionsApi } from '../lib/api';
import type {
  NamedRegion,
  CreateNamedRegionInput,
  UpdateNamedRegionInput,
} from '../types/namedRegion';

interface NamedRegionsState {
  regions: NamedRegion[];
  loading: boolean;
  error: string | null;
  fetchRegions: (stateCode?: string) => Promise<void>;
  createRegion: (input: CreateNamedRegionInput) => Promise<NamedRegion>;
  updateRegion: (id: string, input: UpdateNamedRegionInput) => Promise<NamedRegion>;
  deleteRegion: (id: string) => Promise<void>;
}

export const useNamedRegionsStore = create<NamedRegionsState>((set, get) => ({
  regions: [],
  loading: false,
  error: null,

  fetchRegions: async (stateCode) => {
    set({ loading: true, error: null });
    try {
      const { namedRegions } = await namedRegionsApi.list(
        stateCode ? { stateCode } : undefined,
      );
      set({ regions: namedRegions, loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load named regions';
      set({ loading: false, error: message });
    }
  },

  createRegion: async (input) => {
    const { namedRegion } = await namedRegionsApi.create(input);
    set({ regions: [...get().regions, namedRegion] });
    return namedRegion;
  },

  updateRegion: async (id, input) => {
    const { namedRegion } = await namedRegionsApi.update(id, input);
    set({
      regions: get().regions.map((r) => (r.id === id ? namedRegion : r)),
    });
    return namedRegion;
  },

  deleteRegion: async (id) => {
    await namedRegionsApi.delete(id);
    set({ regions: get().regions.filter((r) => r.id !== id) });
  },
}));
